import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
  Ship,
  Package,
  Plane,
  Truck,
  Layers,
  ChevronRight,
  MapPin,
  Calendar,
  AlertCircle,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Eye,
  X,
} from "lucide-react";


const SERVICE_ICONS = {
  fcl: Ship,
  lcl: Package,
  air: Plane,
  inland: Truck,
  multimodal: Layers,
};

const SERVICE_COLORS = {
  fcl: "bg-blue-100 text-blue-800",
  lcl: "bg-emerald-100 text-emerald-800",
  air: "bg-violet-100 text-violet-800",
  inland: "bg-amber-100 text-amber-800",
  multimodal: "bg-rose-100 text-rose-800",
};

const STATUS_CONFIG = {
  pending: { label: "Pending Review", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  quoted: { label: "Quote Ready", color: "bg-blue-100 text-blue-800", icon: DollarSign },
  accepted: { label: "Accepted", color: "bg-green-100 text-green-800", icon: CheckCircle },
  rejected: { label: "Declined", color: "bg-red-100 text-red-800", icon: XCircle },
  expired: { label: "Expired", color: "bg-gray-100 text-gray-800", icon: AlertCircle },
};

export default function MyQuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ pending: 0, quoted: 0, accepted: 0, action_required: 0 });
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  
  const token = localStorage.getItem("token");

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/quotes/my-quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data.quotes);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error("Failed to load your quotes");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleAccept = async (quoteId) => {
    try {
      await axios.post(`${API_URL}/quotes/${quoteId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote accepted! Our team will contact you shortly.");
      fetchQuotes();
      setSelectedQuote(null);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        toast.error(errorDetail.map(e => e.msg).join(', ') || "Operation failed");
      } else {
        toast.error(typeof errorDetail === 'string' ? errorDetail : "Failed to accept quote");
      }
    }
  };

  const handleReject = async (quoteId) => {
    try {
      await axios.post(`${API_URL}/quotes/${quoteId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote declined");
      fetchQuotes();
      setSelectedQuote(null);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        toast.error(errorDetail.map(e => e.msg).join(', ') || "Operation failed");
      } else {
        toast.error(typeof errorDetail === 'string' ? errorDetail : "Failed to decline quote");
      }
    }
  };

  const filteredQuotes = quotes.filter(q => {
    if (activeTab === "all") return true;
    if (activeTab === "action") return q.status === "quoted";
    return q.status === activeTab;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-foreground mb-2">My Quotes</h1>
            <p className="text-muted-foreground">
              Track your quote requests and respond to offers
            </p>
          </motion.div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-surface border border-border rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{summary.pending}</p>
                  <p className="text-xs text-muted-foreground">Pending Review</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-surface border border-border rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{summary.quoted}</p>
                  <p className="text-xs text-muted-foreground">Quotes Ready</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-surface border border-border rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{summary.accepted}</p>
                  <p className="text-xs text-muted-foreground">Accepted</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className={`bg-surface border rounded-xl p-4 ${summary.action_required > 0 ? "border-primary bg-primary/5" : "border-border"}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${summary.action_required > 0 ? "bg-primary/20" : "bg-gray-100"}`}>
                  <AlertCircle className={`w-5 h-5 ${summary.action_required > 0 ? "text-primary" : "text-gray-600"}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{summary.action_required}</p>
                  <p className="text-xs text-muted-foreground">Action Required</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {[
              { id: "all", label: "All Quotes" },
              { id: "action", label: "Action Required", badge: summary.action_required },
              { id: "pending", label: "Pending" },
              { id: "quoted", label: "Quoted" },
              { id: "accepted", label: "Accepted" },
            ].map((tab) => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveTab(tab.id)}
                className="whitespace-nowrap"
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
                {tab.badge > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-primary-foreground text-primary">
                    {tab.badge}
                  </span>
                )}
              </Button>
            ))}
          </div>

          {/* Quotes List */}
          {loading ? (
            <div className="text-center py-12">
              <Clock className="w-8 h-8 text-muted-foreground animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading your quotes...</p>
            </div>
          ) : filteredQuotes.length === 0 ? (
            <div className="text-center py-12 bg-surface border border-border rounded-xl">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-foreground font-medium mb-2">No quotes found</p>
              <p className="text-sm text-muted-foreground mb-4">
                {activeTab === "all" ? "You haven't submitted any quote requests yet." : "No quotes in this category."}
              </p>
              <Button asChild>
                <a href="/quotes">Request a Quote</a>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredQuotes.map((quote) => {
                const ServiceIcon = SERVICE_ICONS[quote.service_type] || Package;
                const status = STATUS_CONFIG[quote.status] || STATUS_CONFIG.pending;
                const StatusIcon = status.icon;
                
                return (
                  <motion.div
                    key={quote.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-surface border rounded-xl p-4 hover:border-primary/30 transition-colors cursor-pointer ${
                      quote.status === "quoted" ? "border-primary/50 bg-primary/5" : "border-border"
                    }`}
                    onClick={() => setSelectedQuote(quote)}
                    data-testid={`quote-${quote.quote_number}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Left side - Quote info */}
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className={`w-12 h-12 rounded-lg ${SERVICE_COLORS[quote.service_type]} flex items-center justify-center flex-shrink-0`}>
                          <ServiceIcon className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-semibold text-foreground">{quote.quote_number}</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${status.color}`}>
                              {status.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">
                              {quote.origin_city}, {quote.origin_country} → {quote.destination_city}, {quote.destination_country}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(quote.created_at)}
                            </span>
                            <span className="uppercase">{quote.service_type}</span>
                            {quote.container_type && (
                              <span>{quote.container_quantity}x {quote.container_type}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right side - Price/Action */}
                      <div className="text-right flex-shrink-0">
                        {quote.status === "quoted" && quote.quoted_amount ? (
                          <div>
                            <p className="text-2xl font-bold text-primary">
                              {quote.quoted_currency} {quote.quoted_amount?.toLocaleString()}
                            </p>
                            <p className="text-xs text-muted-foreground">Quote valid {quote.quote_validity_days} days</p>
                            <div className="flex gap-2 mt-2">
                              <Button
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleAccept(quote.id); }}
                                className="gap-1"
                                data-testid={`accept-${quote.quote_number}`}
                              >
                                <ThumbsUp className="w-3 h-3" />
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); handleReject(quote.id); }}
                                className="gap-1"
                                data-testid={`reject-${quote.quote_number}`}
                              >
                                <ThumbsDown className="w-3 h-3" />
                                Decline
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <StatusIcon className="w-5 h-5 text-muted-foreground" />
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Quote Detail Modal */}
      {selectedQuote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="font-mono font-bold text-lg">{selectedQuote.quote_number}</span>
                <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_CONFIG[selectedQuote.status]?.color}`}>
                  {STATUS_CONFIG[selectedQuote.status]?.label}
                </span>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSelectedQuote(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Route */}
              <div>
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Route
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Origin</p>
                    <p className="font-medium">{selectedQuote.origin_city}, {selectedQuote.origin_country}</p>
                    {selectedQuote.origin_address && <p className="text-sm text-muted-foreground">{selectedQuote.origin_address}</p>}
                    {selectedQuote.origin_port_airport && <p className="text-sm text-muted-foreground">Port: {selectedQuote.origin_port_airport}</p>}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Destination</p>
                    <p className="font-medium">{selectedQuote.destination_city}, {selectedQuote.destination_country}</p>
                    {selectedQuote.destination_address && <p className="text-sm text-muted-foreground">{selectedQuote.destination_address}</p>}
                    {selectedQuote.destination_port_airport && <p className="text-sm text-muted-foreground">Port: {selectedQuote.destination_port_airport}</p>}
                  </div>
                </div>
              </div>

              {/* Service & Cargo */}
              <div>
                <h3 className="font-semibold text-foreground mb-3">Service Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Service Type</p>
                    <p className="font-medium uppercase">{selectedQuote.service_type}</p>
                  </div>
                  {selectedQuote.container_type && (
                    <div>
                      <p className="text-muted-foreground">Container</p>
                      <p className="font-medium">{selectedQuote.container_quantity}x {selectedQuote.container_type}</p>
                    </div>
                  )}
                  {selectedQuote.total_weight_kg && (
                    <div>
                      <p className="text-muted-foreground">Total Weight</p>
                      <p className="font-medium">{selectedQuote.total_weight_kg} kg</p>
                    </div>
                  )}
                  {selectedQuote.total_volume_cbm && (
                    <div>
                      <p className="text-muted-foreground">Total Volume</p>
                      <p className="font-medium">{selectedQuote.total_volume_cbm} CBM</p>
                    </div>
                  )}
                  {selectedQuote.commodity && (
                    <div>
                      <p className="text-muted-foreground">Commodity</p>
                      <p className="font-medium">{selectedQuote.commodity}</p>
                    </div>
                  )}
                  {selectedQuote.incoterm && (
                    <div>
                      <p className="text-muted-foreground">Incoterm</p>
                      <p className="font-medium">{selectedQuote.incoterm}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Services */}
              {selectedQuote.additional_services && (
                <div>
                  <h3 className="font-semibold text-foreground mb-3">Additional Services</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedQuote.additional_services.pre_carriage && (
                      <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs">Pre-carriage</span>
                    )}
                    {selectedQuote.additional_services.on_carriage && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">On-carriage</span>
                    )}
                    {selectedQuote.additional_services.customs_clearance && (
                      <span className="px-2 py-1 bg-violet-100 text-violet-800 rounded text-xs">
                        Customs ({selectedQuote.additional_services.customs_type})
                      </span>
                    )}
                    {selectedQuote.additional_services.insurance && (
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded text-xs">Insurance</span>
                    )}
                    {selectedQuote.additional_services.warehousing && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">Warehousing</span>
                    )}
                  </div>
                </div>
              )}

              {/* Quote Response */}
              {selectedQuote.status === "quoted" && selectedQuote.quoted_amount && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-primary" />
                    Quote Offer
                  </h3>
                  <p className="text-3xl font-bold text-primary mb-2">
                    {selectedQuote.quoted_currency} {selectedQuote.quoted_amount?.toLocaleString()}
                  </p>
                  {selectedQuote.quote_details && (
                    <p className="text-sm text-muted-foreground mb-3">{selectedQuote.quote_details}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Valid for {selectedQuote.quote_validity_days} days from {formatDate(selectedQuote.quoted_at)}
                  </p>
                  <div className="flex gap-3 mt-4">
                    <Button onClick={() => handleAccept(selectedQuote.id)} className="flex-1 gap-2">
                      <ThumbsUp className="w-4 h-4" />
                      Accept Quote
                    </Button>
                    <Button variant="outline" onClick={() => handleReject(selectedQuote.id)} className="flex-1 gap-2">
                      <ThumbsDown className="w-4 h-4" />
                      Decline
                    </Button>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedQuote.special_requirements && (
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Special Requirements</h3>
                  <p className="text-sm text-muted-foreground bg-gray-50 rounded p-3">
                    {selectedQuote.special_requirements}
                  </p>
                </div>
              )}

              {/* Dates */}
              <div className="text-xs text-muted-foreground border-t pt-4">
                <p>Submitted: {formatDate(selectedQuote.created_at)}</p>
                {selectedQuote.quoted_at && <p>Quoted: {formatDate(selectedQuote.quoted_at)}</p>}
                {selectedQuote.cargo_ready_date && <p>Cargo Ready: {formatDate(selectedQuote.cargo_ready_date)}</p>}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
